import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, switchMap } from 'rxjs';
import {
  RewardService,
  TGetRewardsQueryParams,
} from 'src/app/core/api/reward/reward.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';
import { SortOption } from 'src/app/shared/models/constants/sort-option';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { dedup } from 'src/app/utils/list';
import { RoutesKeys } from 'src/app/utils/routes';
import {
  generateSeriesChips,
  MultiSelectChip,
} from '../filter-container/filter-container.component';
import { ProductService } from 'src/app/core/api/product/product.service';
import { UserService } from 'src/app/core/api/user/user.service';

@Component({
  selector: 'app-rewards',
  templateUrl: './rewards.component.html',
  styleUrls: ['./rewards.component.scss'],
})
/**
 * Rewards Template
 */
export class RewardsComponent implements OnInit, OnDestroy {
  chipContents = [ChipContent.CService(), ChipContent.Products()];

  seriesChips: MultiSelectChip[] = [];

  sortOptions = [
    new ChipContentOption(SortOption.MOST_CLAIMED, 'most-reclaimed'),
    new ChipContentOption(SortOption.LOW_TO_HIGH, 'price-low-to-high'),
    new ChipContentOption(SortOption.HIGH_TO_LOW, 'price-high-to-low'),
    new ChipContentOption(SortOption.A_Z, 'a-to-z'),
    new ChipContentOption(SortOption.Z_A, 'z-to-a'),
  ];

  filters: FilterOptions = {
    sort: SortOption.MOST_CLAIMED,
    cservice: false,
    products: false,
    series: [],
  };

  labels: any = {
    printerFamilyBasename: 'business-series',
  };

  // Family and Business Level Data
  protected availableFamilies: any[] = [];
  availableBusinessLevels: any = {};
  familyMap: any = {};

  rewards: RewardResponseDTO[] = [];
  loadingRewards = false;
  private readonly _params = {
    page: 0,
    pageSize: 25,
    sort: SortOption.MOST_CLAIMED,
  };
  COLOMBIA_REGION_ID = 3;
  snackBarLabels = {
    error: 'unexpected-error',
  };
  addRewardLabel = 'add-reward';
  private currentRegionId: number | null = null;
  private readonly subsink = new Subscription();
  private loadRewards$ = new Subject<void>();

  // prettier-ignore
  /**
   * @constructor
   */
  constructor( // NOSONAR
    private readonly rewardService: RewardService,
    private productService: ProductService,
    private userService: UserService,
    readonly authenticatedUserService: AuthenticatedUserService,
    private readonly snackbarService: SnackBarService,
    private readonly translationService: TranslationService,
    private readonly router: Router,
    private readonly dialogService: DialogService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.snackBarLabels);
    this.translationService.translateObject(this.labels);

    this.productService.getAllFamilyProduct().subscribe({
      next: (productSeriesList) => {
        this.seriesChips = generateSeriesChips(productSeriesList);

        this.availableFamilies = productSeriesList.map((family) => ({
          label: `${this.labels.printerFamilyBasename} ${family.name}`,
          value: family.id,
          family: family.name,
        }));
      },
    });

    this.userService
      .getAllBusinessLevelsGroupedByFamily()
      .subscribe((familiesBusinessLevels) => {
        for (const levelByFamily of familiesBusinessLevels) {
          this.availableBusinessLevels[levelByFamily.familyName] =
            levelByFamily.businessLevels;
          for (const businessLevel of levelByFamily.businessLevels) {
            this.familyMap[businessLevel.id] = businessLevel.familyPk;
          }
        }
      });

    this.subsink.add(
      this.authenticatedUserService.currentRegion$.subscribe((region) => {
        if (!region) return;
        this.currentRegionId = region.id;
        this._params.page = 0;
        this.rewards = [];
        this.subsink.add(
          this.loadRewards$
            .pipe(
              switchMap(() => {
                const params = this.setFilters();
                return this.rewardService.getRewards(params);
              })
            )
            .subscribe({
              next: (moreRewards) => {
                if (moreRewards.length > 0) {
                  this.rewards.push(...moreRewards);
                  this.rewards = dedup(this.rewards, (item) => item.id);
                  if (moreRewards.length === this._params.pageSize) {
                    this._params.page++;
                  }
                }
                this.loadingRewards = false;
              },
              error: () => {
                this.snackbarService.error(this.snackBarLabels.error);
                this.loadingRewards = false;
              },
            })
        );
        this.pullRewards();
      })
    );
  }

  /**
   * Cleans up subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Updates Filters
   * @param {AdminFilters} filters
   */
  updateFilter(filters: FilterOptions) {
    this.filters = filters;
    this._params.page = 0;
    this.rewards = [];
    this.pullRewards();
  }

  /**
   * Fetch more rewards and append to list using Observables
   */
  pullRewards() {
    this.loadingRewards = true;
    this.loadRewards$.next();
  }

  /**
   *
   * @return {TGetRewardsQueryParams}
   */
  setFilters(): TGetRewardsQueryParams {
    let params: TGetRewardsQueryParams = {
      page: this._params.page,
      pageSize: this._params.pageSize,
      sort: this.filters.sort!,
      cservice: true,
      products: true,
    };

    const hasSelectedCouponType =
      this.filters.cservice || this.filters.products;
    if (hasSelectedCouponType) {
      params = {
        ...params,
        cservice: this.filters.cservice!,
        products: this.filters.products!,
      };
    }

    params.familyIds = this.filters.series?.join(',');

    if (this.currentRegionId) {
      params.regionId = this.currentRegionId;
    }

    return params;
  }

  /**
   * Removes deleted reward from the list
   * @param {RewardResponseDTO} reward
   */
  removeReward(reward: RewardResponseDTO) {
    if (!reward) return;
    this.rewards = this.rewards.filter((item) => item.id !== reward.id);
  }

  /**
   * redirects the user to the add rewards screen
   */
  goToRewardAddReward() {
    this.router.navigate([RoutesKeys.REWARD_DETAIL], {
      state: {
        previousPage: RoutesKeys.REWARDS,
      },
    });
  }

  /**
   * Return if rewards list is empty or not
   */
  get isEmpty() {
    return this.rewards.length === 0;
  }

  /**
   * Open rewards info modal when clicked
   * @param {RewardResponseDTO} reward
   */
  onRewardClick(reward: RewardResponseDTO) {
    reward.familiesIds = RewardResponseDTO.getFamilyIds(
      this.familyMap,
      reward.businessLevelIds
    );

    this.dialogService.openRewardInfoModal(
      reward,
      this.availableFamilies,
      this.availableBusinessLevels,
      this.removeReward.bind(this)
    );
  }
}
